import { Copy, Link2, Pin, PinOff, Plus, Trash2, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardBody, CardHeader } from '@/components/ui/card'
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { Segmented } from '@/components/ui/segmented'
import { Textarea } from '@/components/ui/textarea'
import { errorMessage, isApiError } from '@/lib/api/errors'
import type { CheckoutLinkInfo, CustomerDetail, TimelineItem } from '@/lib/api/types'
import { formatDate, formatDateTime, formatRelative, formatUsd } from '@/lib/format'
import {
  type CheckoutLinkInput,
  useAddNote,
  useCreateCheckoutLink,
  useDeleteNote,
  useUpdateCrm,
  useUpdateNote,
} from './api'

/** Tags and the account manager looking after this customer. */
export function CrmCard({ customer: c }: { customer: CustomerDetail }) {
  const update = useUpdateCrm(c.id)
  const [tag, setTag] = useState('')
  const save = (input: { tags?: string[]; account_manager_id?: string | null }, done: string) =>
    update.mutate(input, {
      onSuccess: () => toast.success(done),
      onError: (e) => toast.error(errorMessage(e)),
    })
  const addTag = () => {
    const t = tag.trim().toLowerCase()
    if (!t || c.crm.tags.includes(t)) {
      setTag('')
      return
    }
    save({ tags: [...c.crm.tags, t] }, `Tagged “${t}”.`)
    setTag('')
  }

  return (
    <Card>
      <CardHeader
        title="Account"
        description="For the team only: customers never see tags, notes or who manages them."
      />
      <CardBody className="grid gap-4 text-[13px]">
        <Field label="Account manager" htmlFor="crm-manager">
          <NativeSelect
            id="crm-manager"
            value={c.crm.account_manager?.id ?? ''}
            disabled={update.isPending}
            onChange={(e) =>
              save(
                { account_manager_id: e.target.value || null },
                e.target.value ? 'Account manager assigned.' : 'Account manager removed.',
              )
            }
          >
            <option value="">No one yet</option>
            {c.crm.managers.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </NativeSelect>
        </Field>
        <div className="grid gap-2">
          <p className="text-[13px] font-medium text-ink">Tags</p>
          {c.crm.tags.length > 0 && (
            <ul className="flex flex-wrap gap-1.5" aria-label="Tags">
              {c.crm.tags.map((t) => (
                <li key={t}>
                  <Badge tone="outline" className="gap-1 pr-1">
                    {t}
                    <button
                      type="button"
                      aria-label={`Remove tag ${t}`}
                      className="rounded-full p-0.5 hover:bg-surface-sunken"
                      onClick={() => save({ tags: c.crm.tags.filter((x) => x !== t) }, `Removed “${t}”.`)}
                    >
                      <X className="size-3" />
                    </button>
                  </Badge>
                </li>
              ))}
            </ul>
          )}
          <form
            className="flex gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              addTag()
            }}
          >
            <Input
              aria-label="New tag"
              list="crm-known-tags"
              placeholder="e.g. vip, at risk, beta"
              maxLength={40}
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="h-9 max-w-56 text-[13px]"
            />
            <datalist id="crm-known-tags">
              {c.crm.known_tags
                .filter((t) => !c.crm.tags.includes(t))
                .map((t) => (
                  <option key={t} value={t} />
                ))}
            </datalist>
            <Button
              type="submit"
              size="sm"
              variant="secondary"
              disabled={!tag.trim()}
              loading={update.isPending}
            >
              <Plus /> Add tag
            </Button>
          </form>
        </div>
      </CardBody>
    </Card>
  )
}

/** Internal notes: pinned first, then newest. Authors (and owners and admins) can delete them. */
export function NotesCard({ customer: c }: { customer: CustomerDetail }) {
  const add = useAddNote(c.id)
  const update = useUpdateNote(c.id)
  const remove = useDeleteNote(c.id)
  const [body, setBody] = useState('')

  return (
    <Card>
      <CardHeader
        title="Notes"
        description="Calls, promises and context for whoever helps this customer next."
      />
      <CardBody className="grid gap-4 text-[13px]">
        <form
          className="grid gap-2"
          onSubmit={(e) => {
            e.preventDefault()
            add.mutate(
              { body },
              { onSuccess: () => setBody(''), onError: (err) => toast.error(errorMessage(err)) },
            )
          }}
        >
          <Textarea
            aria-label="New note"
            rows={3}
            maxLength={5000}
            placeholder="What happened, what you promised, what’s next…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          <Button type="submit" size="sm" className="w-fit" disabled={!body.trim()} loading={add.isPending}>
            Add note
          </Button>
        </form>
        {c.crm.notes.length === 0 ? (
          <p className="text-ink-subtle">No notes yet.</p>
        ) : (
          <ul className="grid divide-y divide-border" aria-label="Notes">
            {c.crm.notes.map((n) => (
              <li key={n.id} className="grid gap-1 py-3 first:pt-0">
                <p className="whitespace-pre-wrap text-ink">{n.body}</p>
                <p className="flex flex-wrap items-center gap-2 text-xs text-ink-subtle">
                  {n.pinned && <Badge tone="neutral">Pinned</Badge>}
                  {n.author ?? 'Former staff'} · {formatDateTime(n.created_at)}
                  {n.edited && ' · edited'}
                  <span className="ml-auto flex gap-1">
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={n.pinned ? 'Unpin note' : 'Pin note'}
                      onClick={() =>
                        update.mutate(
                          { noteId: n.id, pinned: !n.pinned },
                          { onError: (e) => toast.error(errorMessage(e)) },
                        )
                      }
                    >
                      {n.pinned ? <PinOff /> : <Pin />}
                    </Button>
                    {n.can_edit && (
                      <Button
                        size="sm"
                        variant="ghost"
                        aria-label="Delete note"
                        onClick={() =>
                          remove.mutate(n.id, {
                            onSuccess: () => toast.success('Note deleted.'),
                            onError: (e) => toast.error(errorMessage(e)),
                          })
                        }
                      >
                        <Trash2 />
                      </Button>
                    )}
                  </span>
                </p>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}

const actionLabels: Record<string, string> = {
  'customer.plan_changed': 'Changed the plan',
  'customer.plan_kept': 'Kept the plan (undid a cancellation or change)',
  'customer.plan_cancelled': 'Cancelled the plan',
  'customer.plan_resumed': 'Resumed the plan',
  'customer.complimentary_plan': 'Set the complimentary plan',
  'customer.complimentary_addons': 'Changed the complimentary add-ons',
  'customer.internal_account': 'Changed whether it’s an internal account',
  'customer.checkout_link_sent': 'Sent a checkout link',
  'customer.email_change_sent': 'Sent an email change link',
  'customer.crm_updated': 'Updated tags or account manager',
  'customer.note_added': 'Added a note',
  'customer.note_deleted': 'Deleted a note',
  'customer.closed': 'Closed the workspace',
  'customer.reopened': 'Reopened the workspace',
  'discount.created': 'Created a discount',
  'discount.removed': 'Removed a discount',
  'invoice.paid': 'Payment received',
  'invoice.refunded': 'Payment refunded',
  'invoice.partial_refund': 'Part of a payment refunded',
  'ticket.opened': 'Opened a support request',
  'billing.checkout_started': 'Started a checkout',
  'billing.complimentary_ended': 'Complimentary plan ended',
  'account.email_changed': 'Changed their sign-in email',
  'member.email_change_sent': 'Sent a member an email change link',
}

const sourceLabels: Record<TimelineItem['source'], string> = {
  customer: 'Customer',
  staff: 'Arva SEO team',
  billing: 'Billing',
  support: 'Support',
}

function humanize(action: string) {
  const words = action.replace(/[._]/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/** Everything that happened with this customer, newest first. */
export function TimelineCard({ customer: c }: { customer: CustomerDetail }) {
  const [source, setSource] = useState<'all' | TimelineItem['source']>('all')
  const items = c.timeline.filter((i) => source === 'all' || i.source === source)

  return (
    <Card className="lg:col-span-2">
      <CardHeader
        title="Timeline"
        description="What the customer did, what the team did for them, payments and support requests."
        actions={
          <Segmented
            label="Show"
            value={source}
            onChange={setSource}
            options={[
              { value: 'all', label: 'All' },
              { value: 'customer', label: 'Customer' },
              { value: 'staff', label: 'Team' },
              { value: 'billing', label: 'Billing' },
              { value: 'support', label: 'Support' },
            ]}
          />
        }
      />
      <CardBody>
        {items.length === 0 ? (
          <p className="text-[13px] text-ink-subtle">Nothing recorded yet.</p>
        ) : (
          <ol className="grid gap-2.5 text-[13px]" aria-label="Timeline">
            {items.map((i, n) => (
              <li key={`${i.at}-${n}`} className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
                <span className="text-xs text-ink-subtle" title={formatDateTime(i.at)}>
                  {formatRelative(i.at)}
                </span>
                <span className="grid gap-0.5">
                  <span className="text-ink">
                    {actionLabels[i.action] ?? humanize(i.action)}
                    {i.detail && <span className="text-ink-muted"> · {i.detail}</span>}
                  </span>
                  <span className="text-xs text-ink-subtle">
                    {sourceLabels[i.source]}
                    {i.by && ` · ${i.by}`}
                  </span>
                </span>
              </li>
            ))}
          </ol>
        )}
      </CardBody>
    </Card>
  )
}

const linkTone: Record<CheckoutLinkInfo['status'], 'success' | 'neutral' | 'outline'> = {
  paid: 'success',
  open: 'neutral',
  expired: 'outline',
}

/** Checkout links support sent for the customer to pay, and a way to send one. */
export function CheckoutLinksCard({ customer: c }: { customer: CustomerDetail }) {
  const [open, setOpen] = useState(false)
  const closed = c.closed_at !== null

  return (
    <Card>
      <CardHeader
        title="Checkout links"
        description="Send the owner a checkout for a new plan or add-on. They pay themselves; nothing is charged until they do."
        actions={
          !closed && (
            <Button size="sm" variant="secondary" onClick={() => setOpen(true)}>
              <Link2 /> New checkout link
            </Button>
          )
        }
      />
      <CardBody>
        {c.checkout_links.length === 0 ? (
          <p className="text-[13px] text-ink-subtle">None sent yet.</p>
        ) : (
          <ul className="grid divide-y divide-border text-[13px]" aria-label="Checkout links">
            {c.checkout_links.map((l) => (
              <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span className="grid gap-0.5">
                  <span className="font-medium text-ink">
                    {l.description}
                    {l.discount_code && <span className="text-ink-muted"> · {l.discount_code}</span>}
                  </span>
                  <span className="text-xs text-ink-subtle">
                    {l.sent_by ?? 'Former staff'} · {formatRelative(l.created_at)}
                    {l.emailed_to ? ` · emailed to ${l.emailed_to}` : ' · not emailed'}
                    {l.status === 'open' && ` · works until ${formatDate(l.expires_at)}`}
                  </span>
                </span>
                <span className="flex items-center gap-2">
                  <Badge tone={linkTone[l.status]}>
                    {l.status === 'open' ? 'Not paid yet' : l.status === 'paid' ? 'Paid' : 'Expired'}
                  </Badge>
                  {l.url && <CopyButton value={l.url} />}
                </span>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
      {open && <CheckoutLinkDialog customer={c} onClose={() => setOpen(false)} />}
    </Card>
  )
}

function CopyButton({ value }: { value: string }) {
  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={() =>
        void navigator.clipboard.writeText(value).then(
          () => toast.success('Link copied.'),
          () => toast.error('Couldn’t copy. Select the link and copy it instead.'),
        )
      }
    >
      <Copy /> Copy link
    </Button>
  )
}

function CheckoutLinkDialog({ customer: c, onClose }: { customer: CustomerDetail; onClose: () => void }) {
  const create = useCreateCheckoutLink(c.id)
  const pm = c.plan_management
  const paid = pm.plans.filter((p) => p.monthly > 0)
  const addons = pm.addons ?? []
  const hasPlan = pm.subscription !== null
  const [kind, setKind] = useState<'plan' | 'addon'>(hasPlan ? 'addon' : 'plan')
  const [plan, setPlan] = useState(paid.at(-1)?.key ?? '')
  const [interval, setInterval] = useState<'monthly' | 'annual'>('monthly')
  const [addon, setAddon] = useState(addons[0]?.code ?? '')
  const [quantity, setQuantity] = useState('1')
  const [code, setCode] = useState('')
  const [note, setNote] = useState('')
  const [email, setEmail] = useState(true)
  const [result, setResult] = useState<CheckoutLinkInfo | null>(null)
  const chosenPlan = paid.find((p) => p.key === plan)
  const chosenAddon = addons.find((a) => a.code === addon)
  const qty = Number(quantity)
  const fieldError = (f: string) => (isApiError(create.error) ? create.error.firstFieldError(f) : undefined)
  const blocked =
    kind === 'plan' && hasPlan
      ? 'They already have a subscription. Owners and admins change it with Change plan on the Plan card.'
      : kind === 'addon' && chosenAddon?.on_plan === false
        ? `${chosenAddon.name} needs a paid plan. Send a plan checkout first.`
        : null

  const submit = () => {
    const input: CheckoutLinkInput =
      kind === 'plan'
        ? {
            kind,
            plan,
            interval,
            email,
            discount_code: code.trim() || undefined,
            note: note.trim() || undefined,
          }
        : {
            kind,
            addon,
            quantity: qty,
            email,
            discount_code: code.trim() || undefined,
            note: note.trim() || undefined,
          }
    create.mutate(input, {
      onSuccess: (link) => {
        setResult(link)
        toast.success(
          link.emailed_to ? `Checkout link emailed to ${link.emailed_to}.` : 'Checkout link ready to copy.',
        )
      },
    })
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        title={`Checkout link for ${c.name}`}
        description={`A Lemon Squeezy checkout for ${c.owner?.email ?? 'the owner'} to pay. It works for 7 days and the plan or add-on applies as soon as it’s paid.`}
      >
        {result ? (
          <div className="grid gap-4 text-[13px]">
            <p className="text-ink">
              <span className="font-semibold">{result.description}</span>
              {result.discount_code && ` with ${result.discount_code}`}.{' '}
              {result.emailed_to ? `Emailed to ${result.emailed_to}.` : 'Not emailed: send it yourself.'}
            </p>
            {result.url && (
              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={result.url}
                  aria-label="Checkout link"
                  className="h-9 text-xs"
                  onFocus={(e) => e.target.select()}
                />
                <CopyButton value={result.url} />
              </div>
            )}
            <DialogClose asChild>
              <Button className="w-fit">Done</Button>
            </DialogClose>
          </div>
        ) : (
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
          >
            <Segmented
              label="What to sell"
              value={kind}
              onChange={setKind}
              options={[
                { value: 'plan', label: 'Plan' },
                { value: 'addon', label: 'Add-on' },
              ]}
            />
            {kind === 'plan' ? (
              <div className="grid gap-3 sm:grid-cols-2">
                <Field label="Plan" htmlFor="cl-plan">
                  <NativeSelect id="cl-plan" value={plan} onChange={(e) => setPlan(e.target.value)}>
                    {paid.map((p) => (
                      <option key={p.key} value={p.key}>
                        {p.name}
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label="Billed" htmlFor="cl-interval">
                  <NativeSelect
                    id="cl-interval"
                    value={interval}
                    onChange={(e) => setInterval(e.target.value as 'monthly' | 'annual')}
                  >
                    <option value="monthly">
                      Monthly{chosenPlan ? ` · ${formatUsd(chosenPlan.monthly)}` : ''}
                    </option>
                    <option value="annual">
                      Yearly{chosenPlan ? ` · ${formatUsd(chosenPlan.annual)}` : ''}
                    </option>
                  </NativeSelect>
                </Field>
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_120px]">
                <Field label="Add-on" htmlFor="cl-addon">
                  <NativeSelect id="cl-addon" value={addon} onChange={(e) => setAddon(e.target.value)}>
                    {addons.map((a) => (
                      <option key={a.code} value={a.code}>
                        {a.name} · {formatUsd(a.monthly_price)} a month
                      </option>
                    ))}
                  </NativeSelect>
                </Field>
                <Field label="Units" htmlFor="cl-qty" error={fieldError('quantity')}>
                  <Input
                    id="cl-qty"
                    type="number"
                    min={1}
                    max={100}
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                  />
                </Field>
                {chosenAddon && Number.isInteger(qty) && qty > 0 && (
                  <p className="text-xs text-ink-subtle sm:col-span-2">
                    {formatUsd(chosenAddon.monthly_price * qty)} a month for {qty}{' '}
                    {qty === 1 ? 'unit' : 'units'}.
                  </p>
                )}
              </div>
            )}
            <Field
              label="Discount code (optional)"
              htmlFor="cl-code"
              error={fieldError('discount_code')}
              hint="An active code from Discounts. It’s applied in the checkout."
            >
              <Input
                id="cl-code"
                list="cl-codes"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                className="uppercase"
              />
              <datalist id="cl-codes">
                {c.discounts
                  .filter((d) => d.status === 'active')
                  .map((d) => (
                    <option key={d.id} value={d.code}>
                      {d.summary}
                    </option>
                  ))}
              </datalist>
            </Field>
            <Field label="Note in the email (optional)" htmlFor="cl-note">
              <Textarea
                id="cl-note"
                rows={2}
                maxLength={500}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Field>
            <label className="flex items-center gap-2 text-[13px] text-ink">
              <input
                type="checkbox"
                className="size-4"
                checked={email}
                onChange={(e) => setEmail(e.target.checked)}
              />
              Email it to {c.owner?.email ?? 'the owner'}
            </label>
            {blocked && (
              <p className="rounded-md bg-surface-sunken px-3 py-2 text-[13px] text-ink-muted">{blocked}</p>
            )}
            {create.isError && !fieldError('discount_code') && !fieldError('quantity') && (
              <p role="alert" className="text-[13px] text-danger">
                {errorMessage(create.error)}
              </p>
            )}
            <div className="flex justify-end gap-2">
              <DialogClose asChild>
                <Button variant="ghost">Cancel</Button>
              </DialogClose>
              <Button
                type="submit"
                loading={create.isPending}
                disabled={blocked !== null || (kind === 'addon' && !(qty >= 1))}
              >
                Create checkout link
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}
